import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useSnackbar } from 'notistack';

function Ayarlar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState([]);

  // Fetch settings from the location_permission table
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('location_permission')
        .select('*')
        .order('id');

      if (error) throw error;
      setSettings(data || []);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setError('Ayarlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Handle toggle change
  const handleToggleChange = async (id, currentValue) => {
    try {
      // Update local state immediately for better UI responsiveness
      const updatedSettings = settings.map((setting) =>
        setting.id === id ? { ...setting, value: !currentValue } : setting
      );
      setSettings(updatedSettings);

      // Then update in the database
      const { error } = await supabase
        .from('location_permission')
        .update({ value: !currentValue })
        .eq('id', id);

      if (error) {
        throw error;
      }

      enqueueSnackbar('Ayar başarıyla güncellendi', { variant: 'success' });
    } catch (error) {
      console.error('Error updating setting:', error);
      enqueueSnackbar('Ayar güncellenirken bir hata oluştu', {
        variant: 'error',
      });
      // Revert the local state change if the update failed
      fetchSettings();
    }
  };

  // Get setting label based on ID
  const getSettingLabel = (id) => {
    switch (id) {
      case 1:
        return 'Konum Zorunlu';
      case 2:
        return 'Hesabımı Sil Butonu';
      default:
        return `Ayar ${id}`;
    }
  };

  // Get setting description based on ID
  const getSettingDescription = (id) => {
    switch (id) {
      case 1:
        return 'Sürücülerin konum paylaşması zorunlu olsun mu?';
      case 2:
        return 'Kullanıcılar hesaplarını silebilsin mi?';
      default:
        return '';
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>{user?.email}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between pb-6">
            <div>
              <h1 className="text-2xl font-medium text-gray-800">Ayarlar</h1>
              <p className="text-sm text-gray-500">
                Sistem ayarlarını buradan yönetebilirsiniz
              </p>
            </div>
          </div>

          {/* Settings List */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {error && (
              <div className="p-4 text-red-700 bg-red-100">{error}</div>
            )}

            {loading ? (
              <div className="p-4 text-center">Ayarlar yükleniyor...</div>
            ) : (
              <div className="divide-y divide-gray-200">
                {settings.map((setting) => (
                  <div key={setting.id} className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {getSettingLabel(setting.id)}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {getSettingDescription(setting.id)}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <div
                          onClick={() =>
                            handleToggleChange(setting.id, setting.value)
                          }
                          className="relative inline-block w-12 h-6 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={setting.value}
                            readOnly
                          />
                          <span
                            className={`absolute inset-0 rounded-full transition duration-200 ${
                              setting.value ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          ></span>
                          <span
                            className={`absolute inset-y-0 left-0 w-5 h-5 mt-0.5 ml-0.5 bg-white rounded-full transition-transform duration-200 transform ${
                              setting.value ? 'translate-x-6' : 'translate-x-0'
                            } shadow`}
                          ></span>
                        </div>
                        <span
                          className={`ml-3 text-sm ${
                            setting.value ? 'text-green-600' : 'text-gray-500'
                          }`}
                        >
                          {setting.value ? 'Açık' : 'Kapalı'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default Ayarlar;
